import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { v: "casa", l: "Trasloco Casa" },
  { v: "uffici", l: "Trasloco Uffici" },
  { v: "autoscale", l: "Noleggio Autoscale" },
  { v: "imballaggio", l: "Imballaggio" },
  { v: "sgombero", l: "Sgombero Magazzini e Case" },
  { v: "nord_italia", l: "Sicilia → Nord Italia" },
  { v: "altro", l: "Altro" },
];

function buildWhatsAppUrl(form) {
  const serviceLabel =
    SERVICES.find((s) => s.v === form.service)?.l || "trasloco";
  const tratta =
    form.origin && form.destination
      ? ` da ${form.origin.trim()} a ${form.destination.trim()}`
      : form.origin
      ? ` da ${form.origin.trim()}`
      : form.destination
      ? ` verso ${form.destination.trim()}`
      : "";

  const lines = [
    `Ciao Sicilia Traslochi, vorrei un preventivo per un ${serviceLabel}${tratta}.`,
    `Mi chiamo ${form.name.trim()}.`,
  ];
  if (form.phone) lines.push(`Telefono: ${form.phone.trim()}`);
  if (form.email) lines.push(`Email: ${form.email.trim()}`);
  if (form.message) lines.push(`Note: ${form.message.trim()}`);

  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${SITE.whatsapp}?text=${text}`;
}

export default function QuoteForm({ variant = "section" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "casa",
    origin: "",
    destination: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [lastWaUrl, setLastWaUrl] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Inserisci nome e telefono per ricevere il preventivo");
      return;
    }

    // Build WhatsApp URL BEFORE the async call so popup is treated as user-initiated
    const waUrl = buildWhatsAppUrl(form);
    // Open immediately to avoid popup blockers
    const waWindow = window.open(waUrl, "_blank", "noopener,noreferrer");
    setLastWaUrl(waUrl);

    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/quotes`, payload);
      toast.success("Richiesta inviata! Apertura WhatsApp in corso...");
      setDone(true);
      setForm({
        name: "",
        phone: "",
        email: "",
        service: "casa",
        origin: "",
        destination: "",
        message: "",
      });
      // Fallback if popup blocked
      if (!waWindow || waWindow.closed) {
        setTimeout(() => {
          window.location.href = waUrl;
        }, 600);
      }
    } catch (err) {
      console.error(err);
      toast.error("Errore nel salvataggio, ma WhatsApp è aperto: completa lì la richiesta.");
    } finally {
      setLoading(false);
    }
  };

  const isHero = variant === "hero";

  return (
    <div
      data-testid={`quote-form-${variant}`}
      className={`relative bg-white border border-brand-line rounded-2xl shadow-brand p-5 sm:p-7 lg:p-8 ${
        isHero ? "" : "max-w-2xl mx-auto"
      } reveal`}
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 sm:left-7 sm:translate-x-0 whitespace-nowrap px-3 py-1 bg-brand-primary text-white text-[10px] tracking-[0.2em] uppercase font-bold rounded">
        Preventivo Gratuito · 60 secondi
      </div>

      <div className="flex items-start justify-between gap-4 mt-1">
        <div>
          <h3 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight text-brand-ink">
            Riceverai una risposta in 60 minuti
          </h3>
          <p className="text-sm text-brand-body mt-1.5">
            Sopralluogo e preventivo <strong>gratuiti</strong>. Nessun impegno.
          </p>
        </div>
      </div>

      {done ? (
        <div data-testid="quote-success-state" className="mt-6 rounded-xl bg-brand-surface border border-brand-line p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center mx-auto">
            <MessageCircle className="w-7 h-7" />
          </div>
          <h4 className="mt-4 font-display font-bold text-xl text-brand-ink">Quasi fatto!</h4>
          <p className="text-sm text-brand-body mt-2">
            Abbiamo aperto WhatsApp con la tua richiesta precompilata.<br />
            <strong className="text-brand-ink">Premi "Invia"</strong> per confermare e ricevere il preventivo.
          </p>
          {lastWaUrl && (
            <Button
              asChild
              className="mt-4 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold"
              data-testid="quote-open-whatsapp-btn"
            >
              <a href={lastWaUrl} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" /> Apri WhatsApp
              </a>
            </Button>
          )}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setDone(false)}
              className="mt-2 text-sm font-semibold text-brand-primary link-underline"
              data-testid="quote-new-request-btn"
            >
              Invia un'altra richiesta
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="quote-form-element">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nome e Cognome *">
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Mario Rossi"
                required
                data-testid="quote-input-name"
              />
            </Field>
            <Field label="Telefono *">
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="3XX XXX XXXX"
                required
                data-testid="quote-input-phone"
              />
            </Field>
          </div>

          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="mario@esempio.it"
              data-testid="quote-input-email"
            />
          </Field>

          <Field label="Servizio richiesto">
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger data-testid="quote-select-service" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.v} value={s.v} data-testid={`quote-service-option-${s.v}`}>
                    {s.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Da (città / via)">
              <Input
                value={form.origin}
                onChange={(e) => update("origin", e.target.value)}
                placeholder="Caltanissetta"
                data-testid="quote-input-origin"
              />
            </Field>
            <Field label="A (città / via)">
              <Input
                value={form.destination}
                onChange={(e) => update("destination", e.target.value)}
                placeholder="Palermo"
                data-testid="quote-input-destination"
              />
            </Field>
          </div>

          <Field label="Note (piani, ascensore, oggetti particolari…)">
            <Textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Es. 3° piano senza ascensore, pianoforte..."
              rows={3}
              data-testid="quote-input-message"
            />
          </Field>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm sm:text-base font-bold transition-colors shadow-lg shadow-[#25D366]/30 whitespace-normal text-center leading-tight"
            data-testid="submit-quote-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Invio in corso...
              </>
            ) : (
              <>
                <MessageCircle className="w-5 h-5 mr-2 shrink-0" />
                <span className="sm:hidden">Invia su WhatsApp</span>
                <span className="hidden sm:inline">Invia su WhatsApp · Preventivo Gratuito</span>
              </>
            )}
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-brand-body pt-1">
            <Lock className="w-3.5 h-3.5" />
            Apriremo WhatsApp con il tuo preventivo precompilato. Nessuno spam.
          </p>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px] tracking-wider uppercase font-semibold text-brand-body">{label}</Label>
      {children}
    </div>
  );
}
